// Watch page — cinematic; auto-redirects to mobile vertical feed on small screens
import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Heart, Send, Hexagon, Sparkles, ShoppingBag, Crown, Scissors, Radio, Share2, Flag } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import FloatingStickers from "../components/FloatingStickers";
import StickerAlert from "../components/StickerAlert";
import HypeMeter from "../components/HypeMeter";
import SponsorBanner from "../components/SponsorBanner";
import LiveKitStreamRoom from "../components/LiveKitStreamRoom";
import StreamTakeoverModal from "../components/StreamTakeoverModal";
import ReportModal from "../components/ReportModal";
import PayPalCheckout from "../components/PayPalCheckout";
import { toast, Toaster } from "sonner";

export default function Watch() {
  const { id } = useParams();
  const { user, refresh } = useAuth();
  const [stream, setStream] = useState(null);
  const [chat, setChat] = useState([]);
  const [presence, setPresence] = useState(null); // live viewer/participant count from WS
  const [stickers, setStickers] = useState([]);
  const [msg, setMsg] = useState("");
  const [trigger, setTrigger] = useState(null);
  const [alert, setAlert] = useState(null);
  const [tipAmount, setTipAmount] = useState(5);
  const [liked, setLiked] = useState(false);
  const chatRef = useRef(null);
  const nav = useNavigate();

  // COMPULSORY mobile-first: auto-redirect small screens to the vertical feed
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.innerWidth < 768) {
      nav(`/feed?stream=${id}`, { replace: true });
    }
  }, [id, nav]);
  const [clipOpen, setClipOpen] = useState(false);
  const [clipTitle, setClipTitle] = useState("");

  // === Request-to-Co-Stream state ===
  const [cohostModalOpen, setCohostModalOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [cohostState, setCohostState] = useState("none"); // none | pending | accepted | declined | superseded

  useEffect(() => {
    api.get(`/streams/${id}`).then(({ data }) => { setStream(data.stream); setChat(data.chat); });
    api.get("/stickers").then(({ data }) => setStickers(data));
  }, [id]);

  // Raid listener — owner raid redirects viewer to target stream
  useEffect(() => {
    if (!id) return;
    const i = setInterval(async () => {
      try {
        const { data } = await api.get(`/raids/active?stream_id=${id}`);
        const r = data.find((x) => x.target_stream_id && x.target_stream_id !== id);
        if (r) {
          toast.success(`⚡ OWNER RAID — ${r.message}`);
          setTimeout(() => nav(`/watch/${r.target_stream_id}`), 1800);
        }
      } catch {}
    }, 12000);
    return () => clearInterval(i);
  }, [id, nav]);

  // Request-to-Co-Stream: poll status every 5s once a request has been sent.
  // On "accepted", flip cohostState so LiveKitStreamRoom remounts with role="cohost".
  useEffect(() => {
    if (!user || !id) return;
    if (cohostState !== "pending") return;
    let alive = true;
    const iv = setInterval(async () => {
      try {
        const { data } = await api.get(`/cohost/status/${id}`);
        if (!alive) return;
        const s = data?.state;
        if (s === "accepted") {
          setCohostState("accepted");
          toast.success("You're on-air — cohost mic + camera enabled");
          clearInterval(iv);
        } else if (s === "declined" || s === "superseded") {
          setCohostState(s);
          toast.info(s === "declined" ? "Cohost request declined" : "Cohost request superseded");
          clearInterval(iv);
        }
      } catch { /* keep polling */ }
    }, 5000);
    return () => { alive = false; clearInterval(iv); };
  }, [id, user, cohostState]);

  // WebSocket realtime chat — with 20s keepalive ping + auto-reconnect on drop,
  // so intermediate proxies (which routinely close idle sockets after ~30s)
  // don't leave the client stuck with a dead connection.
  useEffect(() => {
    if (!id) return;
    let ws;
    let pingTimer = null;
    let reconnectTimer = null;
    let closedByUnmount = false;

    const handleMessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        if (msg.type === "chat") setChat((c) => (msg.payload?.id && c.some((x) => x.id === msg.payload.id)) ? c : [...c, msg.payload].slice(-200));
        if (msg.type === "presence") setPresence(msg.count);
        if (msg.type === "sticker") {
          setTrigger({ emoji: msg.payload.emoji, color: msg.payload.color,
                       animation: msg.payload.animation, sound_url: msg.payload.sound_url,
                       price: msg.payload.price, burst: msg.payload.burst || 3, key: Date.now() });
        }
      } catch {}
    };

    const open = () => {
      if (closedByUnmount) return;
      try {
        const wsUrl = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws") + `/api/ws/chat/${id}`;
        ws = new WebSocket(wsUrl);
        ws.onopen = () => {
          if (pingTimer) clearInterval(pingTimer);
          pingTimer = setInterval(() => {
            try { if (ws && ws.readyState === 1) ws.send(JSON.stringify({ type: "ping" })); } catch { /* noop */ }
          }, 20000);
        };
        ws.onmessage = handleMessage;
        ws.onerror = () => { try { ws.close(); } catch {} };
        ws.onclose = () => {
          if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
          if (closedByUnmount) return;
          if (reconnectTimer) clearTimeout(reconnectTimer);
          reconnectTimer = setTimeout(open, 1500);
        };
      } catch {}
    };

    open();

    return () => {
      closedByUnmount = true;
      if (pingTimer) clearInterval(pingTimer);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try { ws?.close(); } catch {}
    };
  }, [id]);

  // Track watch-minutes for quests (every 60s while watching)
  useEffect(() => {
    if (!user) return;
    const i = setInterval(() => {
      api.post("/quests/track", { event_type: "watch_minutes", value: 1 }).catch(() => {});
    }, 60000);
    return () => clearInterval(i);
  }, [user, id]);

  // Sticky auto-scroll: only follow the newest message if the user was
  // already within 80px of the bottom. If they've scrolled up to read
  // history we leave them alone — they can tap "New messages ↓" (below)
  // to jump back down.
  const [chatAtBottom, setChatAtBottom] = useState(true);
  useEffect(() => {
    const el = chatRef.current;
    if (!el) return;
    if (chatAtBottom) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [chat, chatAtBottom]);
  const onChatScroll = () => {
    const el = chatRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    setChatAtBottom(nearBottom);
  };
  const jumpToLatestChat = () => {
    const el = chatRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    setChatAtBottom(true);
  };

  const sendChat = async () => {
    if (!msg.trim()) return;
    if (!user) { toast.error("Sign in to chat."); return; }
    // Safety pre-check: kid swear filter + platform-wide nudity ban
    try {
      const { data: safety } = await api.post("/safety/chat-check", { text: msg });
      if (safety.blocked) {
        const reason = safety.reason || "blocked";
        if (reason === "nudity") toast.error("⛔ Nudity / sexual language banned. Your account has been flagged for review.");
        else if (reason === "swear_warning") toast.error("⚠️ Watch your language — your parent has been notified (warning 1).");
        else if (reason === "swear_escalated") toast.error("❌ Repeated swearing — this has been escalated to staff.");
        else toast.error("Message blocked.");
        return;
      }
    } catch {}
    const { data } = await api.post(`/streams/${id}/chat`, { stream_id: id, message: msg });
    // Local optimistic append — guarded by id so we don't double-render if the
    // server's WebSocket broadcast has already added this same message.
    setChat((c) => (data?.id && c.some((x) => x.id === data.id)) ? c : [...c, data]);
    setMsg("");
  };

  const playStickerSound = (sticker) => {
    // Sound is handled inside FloatingStickers now; this is kept as no-op for back-compat
    return;
  };

  const sendSticker = async (sticker) => {
    setTrigger({
      emoji: sticker.emoji, color: sticker.color,
      animation: sticker.animation, sound_url: sticker.sound_url,
      price: sticker.price,
      burst: sticker.burst || (sticker.tier === "mega" ? 1 : sticker.tier === "elite" ? 1 : sticker.tier === "premium" ? 6 : sticker.tier === "paid" ? 5 : 4),
      key: Date.now(),
    });
    // Post a small chat-bubble for every sticker send (always)
    const senderName = user?.display_name || "Guest";
    const chatBubble = {
      id: `local-${Date.now()}`,
      user_name: senderName,
      message: `sent ${sticker.emoji} ${sticker.name}${sticker.price > 0 ? ` · ${sticker.price} NX` : ""}`,
      is_sticker_event: true,
      sticker_color: sticker.color,
      ts: new Date().toISOString(),
    };
    setChat((c) => [...c, chatBubble].slice(-200));
    // Broadcast to peers
    try {
      const wsUrl = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws") + `/api/ws/chat/${id}`;
      const ws = new WebSocket(wsUrl);
      ws.onopen = () => {
        ws.send(JSON.stringify({ type: "sticker", payload: { emoji: sticker.emoji, color: sticker.color, animation: sticker.animation, sound_url: sticker.sound_url, price: sticker.price, burst: sticker.burst || 3 } }));
        ws.send(JSON.stringify({ type: "chat", payload: chatBubble }));
        setTimeout(() => ws.close(), 200);
      };
    } catch {}
    // For paid stickers, also fire the small corner alert
    if (sticker.price > 0) {
      if (!user) { toast.error("Sign in to send paid stickers."); return; }
      // Pre-flight: if balance is short, prompt to buy NX bundle (no server call)
      if ((user.wallet_balance || 0) < sticker.price) {
        toast.error(`You need ${sticker.price} NX for ${sticker.name}. Redirecting to top-up…`);
        setTimeout(() => nav(`/coins?need=${sticker.price}&for=sticker`), 1100);
        return;
      }
      try {
        await api.post("/tips", { stream_id: id, amount: sticker.price, sticker_id: sticker.id, message: `${sticker.name} sticker` });
        setAlert({
          emoji: sticker.emoji, color: sticker.color, name: sticker.name,
          tier: sticker.tier, price: sticker.price, user_name: user.display_name, key: Date.now(),
        });
        refresh();
      } catch (e) {
        const d = e?.response?.data?.detail;
        if (d && d.code === "INSUFFICIENT_NX") {
          toast.error(d.message || "Not enough NX. Buy a bundle.");
          setTimeout(() => nav(`/coins?need=${d.needed}&for=sticker`), 1000);
        } else {
          toast.error(d || "Could not send");
        }
      }
    }
  };

  const sendTip = async () => {
    if (!user) { toast.error("Sign in to tip."); return; }
    try {
      await api.post("/tips", { stream_id: id, amount: parseFloat(tipAmount) });
      toast.success(`${tipAmount} NX tipped to ${stream.creator_name}`);
      setTrigger({ emoji: "💎", color: "#00F0FF", animation: "diamond-rain", burst: 8, key: Date.now() });
      setAlert({ emoji: "💎", color: "#00F0FF", name: "Diamond Tip", tier: "tip", price: tipAmount, user_name: user.display_name, key: Date.now() });
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Tip failed"); }
  };

  // Single supporter tier: $4.90 AUD one-time payment via PayPal/Google Pay, 30 days
  // of access, no auto-renewal -- opens a checkout modal rather than an instant NX charge.
  const [showSupportModal, setShowSupportModal] = useState(false);
  const subscribe = () => {
    if (!user) { toast.error("Sign in to become a supporter."); return; }
    setShowSupportModal(true);
  };

  if (!stream) return <div className="p-20 text-center text-white/40">Tuning into stream…</div>;

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" data-testid="watch-page">
      <Toaster theme="dark" position="top-center" />
      {/* Request-to-Co-Stream modal — no auto-open; triggered by the button below */}
      <StreamTakeoverModal
        streamId={stream.id}
        streamCreatorId={stream.creator_id}
        open={cohostModalOpen}
        onClose={() => setCohostModalOpen(false)}
        onRequested={() => setCohostState("pending")}
      />
      {reportOpen && <ReportModal targetType="stream" targetId={stream.id} onClose={() => setReportOpen(false)} />}

      {showSupportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-6" onClick={() => setShowSupportModal(false)}>
          <div className="glass-strong p-6 max-w-md w-full space-y-4" onClick={(e) => e.stopPropagation()} data-testid="supporter-checkout-modal">
            <h3 className="font-display text-2xl">Become a supporter</h3>
            <p className="text-xs text-white/50">$4.90 AUD · one-time payment · 30 days of supporter status for {stream.creator_name} · no auto-renewal, resubscribe any time it expires.</p>
            <PayPalCheckout
              purpose="creator_sub"
              amountUSD={4.90}
              refId={stream.creator_id}
              label="Become a Supporter"
              onSuccess={() => { toast.success(`You're now supporting ${stream.creator_name} for 30 days!`); setShowSupportModal(false); refresh(); }}
            />
          </div>
        </div>
      )}
      {/* PLAYER + INFO */}
      <div className="flex-1 min-w-0">
        <div className="relative aspect-video bg-black overflow-hidden">
          {stream.livekit_room ? (
            <LiveKitStreamRoom streamId={stream.id} role={user?.id === stream.creator_id ? "self_view" : (cohostState === "accepted" ? "cohost" : "viewer")}
              fallback={<video src={stream.video_url || "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4"} className="w-full h-full object-cover" autoPlay loop muted playsInline />} />
          ) : (
            <video src={stream.video_url} className="w-full h-full object-cover" autoPlay loop muted playsInline />
          )}
          <div className="absolute top-4 left-4 flex items-center gap-3 z-30">
            <div className="bg-nexus-crimson px-3 py-1.5 text-[10px] tracking-[0.3em] font-display uppercase animate-pulse">● Live</div>
            <div className="text-[11px] text-white/70 font-display tracking-wide">{stream.viewers.toLocaleString()} viewers</div>
            {presence !== null && (
              <div
                className="bg-black/60 backdrop-blur-sm border border-nexus-cyan/50 text-nexus-cyan px-2.5 py-1 text-[10px] tracking-[0.25em] uppercase font-display flex items-center gap-1.5 rounded-full"
                data-testid="presence-pill"
                title="Live participants (chat + video)"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-nexus-cyan animate-pulse" />
                {presence} here now
              </div>
            )}
            {stream.livekit_room && <div className="bg-nexus-mint/20 text-nexus-mint border border-nexus-mint/40 px-2 py-1 text-[9px] tracking-[0.3em] uppercase">LiveKit</div>}
            {user && user.id === stream.creator_id && stream.is_live && (
              <button
                onClick={async () => {
                  if (!window.confirm("End this live stream? Viewers will be dropped and this can't be undone.")) return;
                  try {
                    await api.post(`/studio/end/${stream.id}`);
                    toast.success("Stream ended.");
                    setStream((s) => ({ ...s, is_live: false }));
                    setTimeout(() => nav("/studio"), 900);
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Couldn't end stream");
                  }
                }}
                data-testid="end-stream-btn"
                className="bg-nexus-crimson hover:bg-nexus-crimson/80 text-white px-3 py-1.5 rounded text-[10px] tracking-[0.3em] font-display font-bold uppercase transition-colors border border-nexus-crimson/60 shadow"
                title="Only you can see this button"
              >
                ■ End Stream
              </button>
            )}
          </div>
          <div className="absolute top-4 right-4 flex gap-2 z-30">
            {user && (
              <button onClick={() => setReportOpen(true)} className="bg-black/60 hover:bg-nexus-crimson/30 px-3 py-1.5 text-[10px] tracking-[0.3em] uppercase border border-white/20" data-testid="report-stream-btn"><Flag className="w-3 h-3 inline mr-1" />Report</button>
            )}
            <button onClick={() => setClipOpen(true)} className="bg-black/60 hover:bg-nexus-cyan/30 px-3 py-1.5 text-[10px] tracking-[0.3em] uppercase border border-white/20" data-testid="clip-btn"><Scissors className="w-3 h-3 inline mr-1" />Clip</button>
            {user && user.id !== stream.creator_id && cohostState !== "accepted" && (
              <button
                onClick={() => setCohostModalOpen(true)}
                disabled={cohostState === "pending"}
                className="bg-nexus-magenta/20 hover:bg-nexus-magenta/40 text-nexus-magenta px-3 py-1.5 text-[10px] tracking-[0.3em] uppercase border border-nexus-magenta/40 disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="request-cohost-btn"
              >
                {cohostState === "pending" ? "Requested…" : "Request to Join"}
              </button>
            )}
            {cohostState === "accepted" && (
              <div className="bg-nexus-mint/20 text-nexus-mint border border-nexus-mint/40 px-3 py-1.5 text-[10px] tracking-[0.3em] uppercase" data-testid="cohost-live-badge">● Cohost Live</div>
            )}
            {user?.role === "owner" && (
              <button onClick={async () => {
                try { await api.post("/owner/raid", { target_stream_id: id, message: "Owner raid — drop in!" }); toast.success("Raid fired · viewers across NEXUS are converging on this stream"); }
                catch (e) { toast.error(e?.response?.data?.detail || "Raid failed"); }
              }} className="bg-nexus-amber/20 hover:bg-nexus-amber/40 text-nexus-amber px-3 py-1.5 text-[10px] tracking-[0.3em] uppercase border border-nexus-amber/40" data-testid="owner-raid-btn"><Radio className="w-3 h-3 inline mr-1" />Raid Here</button>
            )}
          </div>
          <FloatingStickers stickers={stickers} trigger={trigger} />
          <StickerAlert alert={alert} />
          <HypeMeter streamId={id} isOwnerOrCreator={user?.id === stream.creator_id} currentUserRole={user?.role} />

          {/* MOBILE-ONLY: TikTok-style chat overlay floating on top of the stream.
              Same semi-transparent white/blur bubble treatment used on VerticalFeed
              so bubbles don't cover the video, message text stays white/legible via
              inline color (bypasses global body.theme-light !important override),
              and long messages wrap fully instead of being clipped. */}
          <div className="lg:hidden absolute left-3 right-20 bottom-28 z-30 pointer-events-none flex flex-col-reverse gap-1.5 max-h-[35vh] overflow-hidden" data-testid="watch-chat-overlay">
            {chat.slice(-8).slice().reverse().map((m) => (
              <div
                key={m.id}
                className="bg-white/15 backdrop-blur-md rounded-2xl px-3.5 py-1.5 text-sm flex flex-wrap items-baseline gap-x-2 gap-y-0.5 self-start max-w-[90%] shadow-lg"
                style={{ textShadow: "0 1px 2px rgba(0,0,0,0.9), 0 0 4px rgba(0,0,0,0.6)", color: "#ffffff" }}
              >
                <span
                  className="font-display tracking-wider text-[12px] font-semibold shrink-0"
                  style={{ color: m.is_bot ? "#5EEAD4" : "#F4C542" }}
                >{m.user_name}</span>
                <span className="font-semibold whitespace-pre-wrap break-words" style={{ color: "#ffffff" }}>{m.message}</span>
              </div>
            ))}
          </div>
          {/* MOBILE-ONLY: TikTok-style transparent chat input */}
          <form
            onSubmit={(e) => { e.preventDefault(); sendChat(); }}
            className="lg:hidden absolute left-3 right-16 bottom-3 z-40 flex gap-2"
            data-testid="watch-chat-overlay-form"
          >
            <input
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              placeholder={user ? "Say something…" : "Sign in to chat"}
              className="flex-1 bg-black/40 backdrop-blur-md text-white text-sm placeholder-white/60 rounded-full px-4 py-2 border border-white/20 focus:border-nexus-gold/60 focus:outline-none"
              data-testid="watch-chat-overlay-input"
            />
            <button type="submit" className="bg-nexus-gold text-black rounded-full w-10 h-10 flex items-center justify-center font-bold shadow-lg active:scale-95 transition" data-testid="watch-chat-overlay-send">→</button>
          </form>

          {/* TIKTOK-STYLE FLOATING ACTION RAIL — visible on every stream */}
          <div className="absolute right-2 lg:right-3 bottom-20 lg:bottom-6 z-30 flex flex-col gap-3 items-center" data-testid="stream-action-rail">
            <button onClick={() => window.open(`/u/${stream.creator_id}`, "_blank")} className="flex flex-col items-center gap-0.5" data-testid="rail-profile" title="Open creator profile">
              <img src={stream.creator_avatar || `https://api.dicebear.com/7.x/identicon/svg?seed=${stream.creator_id}`} alt="" className="w-10 h-10 rounded-full ring-2 ring-nexus-gold object-cover" />
              <span className="text-[8px] drop-shadow" style={{ color: "rgba(255,255,255,0.9)" }}>Profile</span>
            </button>
            <button onClick={() => setLiked((v) => !v)} className="flex flex-col items-center gap-0.5" data-testid="rail-like" title="Like">
              <div className="bg-black/60 backdrop-blur-md w-9 h-9 rounded-full flex items-center justify-center">
                <Heart className="w-4 h-4 drop-shadow-lg" style={{ color: "#ffffff", fill: liked ? "#ff2e8a" : "none" }} />
              </div>
              <span className="text-[8px] drop-shadow" style={{ color: "rgba(255,255,255,0.9)" }}>Like</span>
            </button>
            <button onClick={subscribe} className="flex flex-col items-center gap-0.5" data-testid="rail-follow" title="Become a supporter">
              <div className="bg-black/60 backdrop-blur-md w-9 h-9 rounded-full flex items-center justify-center">
                <Crown className="w-4 h-4 drop-shadow-lg" style={{ color: "#F4C542" }} />
              </div>
              <span className="text-[8px] drop-shadow" style={{ color: "rgba(255,255,255,0.9)" }}>Follow</span>
            </button>
            <button onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/watch/${id}`); toast.success("Link copied"); }} className="flex flex-col items-center gap-0.5" data-testid="rail-share" title="Share">
              <div className="bg-black/60 backdrop-blur-md w-9 h-9 rounded-full flex items-center justify-center">
                <Share2 className="w-4 h-4 drop-shadow-lg" style={{ color: "#ffffff" }} />
              </div>
              <span className="text-[8px] drop-shadow" style={{ color: "rgba(255,255,255,0.9)" }}>Share</span>
            </button>
          </div>
        </div>

        {/* Sponsor banner BELOW every stream — owner included so everyone earns */}
        <SponsorBanner surface="stream" creatorId={stream.creator_id} hideForOwner={false} />

        <div className="p-6 lg:p-8 space-y-6">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <h1 className="font-display font-black text-2xl lg:text-3xl tracking-tight">{stream.title}</h1>
              <div className="flex items-center gap-4 mt-4">
                <Link to={`/u/${stream.creator_id}`} className="flex items-center gap-4 hover:opacity-80" data-testid="creator-link">
                  <img src={stream.creator_avatar} alt="" className="w-12 h-12 ring-1 ring-white/20" />
                  <div>
                    <div className="font-display font-semibold">{stream.creator_name}</div>
                    <div className="text-[11px] text-white/40 tracking-wider">view profile · 1,240 followers</div>
                  </div>
                </Link>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <button className="btn-ghost" data-testid="follow-btn"><Heart className="w-3.5 h-3.5" /> Follow</button>
              <button onClick={subscribe} className="btn-nexus" data-testid="subscribe-btn"><Crown className="w-3.5 h-3.5" /> Subscribe $4.99</button>
            </div>
          </div>

          {/* Tip jar */}
          <div className="glass p-5 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-1">
              <div className="label-eyebrow mb-1">// tip jar</div>
              <div className="text-sm text-white/60">Send NX coins directly to {stream.creator_name}. 95% to creator.</div>
            </div>
            <input type="number" value={tipAmount} onChange={(e) => setTipAmount(e.target.value)} className="w-24 bg-transparent border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-nexus-cyan" data-testid="tip-amount" />
            <button onClick={sendTip} className="btn-nexus" data-testid="send-tip-btn"><Sparkles className="w-3.5 h-3.5" /> Tip NX</button>
          </div>

          {/* Sticker shop */}
          <div className="space-y-4" data-testid="sticker-shop">
            <div className="flex items-center justify-between">
              <div className="label-eyebrow">// sticker shop</div>
              <div className="text-[11px] text-white/40 tracking-wider">Free emojis float · paid stickers trigger animated alerts.</div>
            </div>
            <div className="grid grid-cols-5 sm:grid-cols-10 gap-2">
              {stickers.map((st) => (
                <button key={st.id} onClick={() => sendSticker(st)} data-testid={`sticker-${st.name}`} className="aspect-square border border-white/10 hover:border-white/30 flex flex-col items-center justify-center gap-1 group transition-colors">
                  <div className="text-2xl group-hover:scale-110 transition-transform" style={{ filter: `drop-shadow(0 0 8px ${st.color})` }}>{st.emoji}</div>
                  <div className="text-[8px] tracking-[0.2em] uppercase font-display" style={{ color: st.color }}>{st.price > 0 ? `${st.price} NX` : "free"}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* CHAT — dark backdrop so the mobile-style semi-transparent white bubbles
          and pure-white message text stay legible even under the app-wide
          body.theme-light. */}
      <aside
        className="w-full lg:w-[380px] border-l border-white/10 flex flex-col h-[80vh] lg:h-screen sticky top-16"
        data-testid="live-chat"
        style={{ backgroundColor: "#0a0a0a" }}
      >
        <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
          <div className="font-display text-xs tracking-[0.3em]" style={{ color: "#ffffff" }}>LIVE CHAT</div>
          <div className="text-[10px] tracking-wider" style={{ color: "rgba(255,255,255,0.55)" }}>{stream.viewers.toLocaleString()}</div>
        </div>
        <div ref={chatRef} onScroll={onChatScroll} className="flex-1 overflow-y-auto px-4 py-3 space-y-2 relative">
          {chat.length === 0 && <div className="text-xs text-center py-10" style={{ color: "rgba(255,255,255,0.4)" }}>No messages yet — be the first to say something.</div>}
          {chat.map((m) => (
            <div
              key={m.id}
              className="text-sm leading-snug rounded-2xl px-3 py-1.5 backdrop-blur-md inline-flex flex-wrap items-baseline gap-x-2 gap-y-0.5 max-w-full"
              data-testid="chat-line"
              style={{ backgroundColor: "rgba(255,255,255,0.10)", color: "#ffffff" }}
            >
              <span
                className="font-display font-semibold text-xs tracking-wider shrink-0"
                style={{ color: m.is_sticker_event ? m.sticker_color : (m.is_bot ? "#5EEAD4" : "#00F0FF") }}
              >{m.user_name}</span>
              <span
                className={m.is_sticker_event ? "italic text-xs" : "font-semibold"}
                style={{ color: m.is_sticker_event ? "rgba(255,255,255,0.75)" : "#ffffff", whiteSpace: "pre-wrap", wordBreak: "break-word" }}
              >{m.message}</span>
            </div>
          ))}
        </div>
        {!chatAtBottom && (
          <button
            onClick={jumpToLatestChat}
            data-testid="chat-jump-latest"
            className="absolute bottom-16 right-4 z-10 bg-nexus-cyan text-black text-[10px] tracking-widest uppercase font-display font-bold px-3 py-1.5 rounded-full shadow-lg hover:bg-nexus-cyan/90 transition-colors"
          >
            New messages ↓
          </button>
        )}
        <div className="border-t border-white/10 p-3 flex gap-2">
          <input
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendChat()}
            placeholder="say something…"
            className="flex-1 bg-transparent border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-nexus-cyan"
            data-testid="chat-input"
            style={{ color: "#ffffff" }}
          />
          <button onClick={sendChat} className="btn-nexus !py-2 !px-3" data-testid="send-chat"><Send className="w-3.5 h-3.5" /></button>
        </div>
      </aside>

      {clipOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-6" onClick={() => setClipOpen(false)}>
          <div className="glass-strong p-6 max-w-md w-full space-y-4" onClick={(e) => e.stopPropagation()} data-testid="clip-modal">
            <h3 className="font-display text-xl flex items-center gap-2"><Scissors className="w-5 h-5 text-nexus-cyan" /> Clip this moment</h3>
            <p className="text-xs text-white/50">Save a 30-second clip from this stream to the creator's Library.</p>
            <input value={clipTitle} onChange={(e) => setClipTitle(e.target.value)} placeholder="Clip title (e.g. 'Insane 1v3 clutch')" className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm" data-testid="clip-title-input" />
            <button onClick={async () => {
              if (!clipTitle.trim()) { toast.error("Title required"); return; }
              try {
                await api.post("/clips", { stream_id: id, title: clipTitle, start_seconds: 0, end_seconds: 30 });
                toast.success("Clip saved!"); setClipOpen(false); setClipTitle("");
              } catch (e) { toast.error(e?.response?.data?.detail || "Clip failed"); }
            }} className="btn-nexus w-full justify-center" data-testid="clip-save">Save clip</button>
          </div>
        </div>
      )}
    </div>
  );
}
